import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore,
  ExpandLess,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';

/**
 * ValidationErrorDisplay Component
 * 
 * Displays structured validation errors in a user-friendly format.
 * Handles job description validation errors with:
 * - Critical issues (blocking)
 * - Warnings (non-blocking)
 * - Suggestions (actionable advice)
 * - Metadata (technical details)
 */
const ValidationErrorDisplay = ({ error, onDismiss }) => {
  const [showMetadata, setShowMetadata] = useState(false);

  // Try to parse error message if it's a JSON string
  let errorData = null;
  if (typeof error === 'string') {
    try {
      // Check if error message contains JSON
      const jsonMatch = error.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        errorData = JSON.parse(jsonMatch[0]);
      }
    } catch (e) {
      // If parsing fails, treat as plain error message
      console.warn('Failed to parse error as JSON:', e);
    }
  } else if (error && typeof error === 'object') {
    errorData = error;
  }

  // If we have structured error data, display it nicely
  if (errorData && errorData.validation) {
    const { validation, code, message } = errorData;
    const { issues = [], warnings = [], suggestions = [], metadata = {} } = validation;

    return (
      <Paper
        elevation={3}
        sx={{
          mt: 3,
          mb: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: '2px solid',
          borderColor: 'error.main',
          bgcolor: '#fff5f5',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <ErrorIcon sx={{ fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
              Job Description Validation Failed
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Please fix the issues below before proceeding with the analysis
            </Typography>
          </Box>
          {onDismiss && (
            <IconButton
              onClick={onDismiss}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <ExpandLess />
            </IconButton>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* Quality Score */}
          {validation.quality !== undefined && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: validation.quality < 50 ? 'error.main' : validation.quality < 70 ? 'warning.main' : 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                }}
              >
                {validation.quality}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Quality Score
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {validation.quality < 50
                    ? 'Poor - Critical issues found'
                    : validation.quality < 70
                    ? 'Fair - Needs improvement'
                    : 'Good - Minor issues'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Critical Issues */}
          {issues.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="error.main"
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ErrorIcon />
                Critical Issues ({issues.length})
              </Typography>
              <List sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'error.light' }}>
                {issues.map((issue, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        py: 2,
                        '&:hover': { bgcolor: '#fff5f5' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={600} color="error.main">
                            {issue.message}
                          </Typography>
                        }
                        secondary={
                          issue.current !== undefined && issue.required !== undefined ? (
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={`Current: ${issue.current}`}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                              <Chip
                                label={`Required: ${issue.required}`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          ) : issue.hint ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              💡 {issue.hint}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                    {index < issues.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="warning.main"
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <WarningIcon />
                Suggestions for Improvement ({warnings.length})
              </Typography>
              <List sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'warning.light' }}>
                {warnings.map((warning, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        py: 2,
                        '&:hover': { bgcolor: '#fffbf0' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={600} color="warning.dark">
                            {warning.message}
                          </Typography>
                        }
                        secondary={
                          warning.hint ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              💡 {warning.hint}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                    {index < warnings.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="info.main"
                sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <LightbulbIcon />
                Actionable Tips ({suggestions.length})
              </Typography>
              <List sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'info.light' }}>
                {suggestions.map((suggestion, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        py: 1.5,
                        '&:hover': { bgcolor: '#f0f7ff' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.primary">
                            {suggestion}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < suggestions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}

          {/* Metadata (Collapsible) */}
          {metadata && Object.keys(metadata).length > 0 && (
            <Accordion
              expanded={showMetadata}
              onChange={() => setShowMetadata(!showMetadata)}
              sx={{
                mt: 2,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: '#f8f9fa',
                  borderRadius: showMetadata ? '8px 8px 0 0' : '8px',
                  '&:hover': { bgcolor: '#e9ecef' },
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Technical Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fff', pt: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  {metadata.length !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Length
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {metadata.length} characters
                      </Typography>
                    </Box>
                  )}
                  {metadata.wordCount !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Word Count
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {metadata.wordCount} words
                      </Typography>
                    </Box>
                  )}
                  {metadata.skillCount !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Skills Detected
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {metadata.skillCount} skills
                      </Typography>
                    </Box>
                  )}
                  {metadata.sectionsFound !== undefined && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Sections Found
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {metadata.sectionsFound} sections
                      </Typography>
                    </Box>
                  )}
                  {metadata.extractedSkills && metadata.extractedSkills.length > 0 && (
                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Extracted Skills
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {metadata.extractedSkills.slice(0, 20).map((skill, idx) => (
                          <Chip key={idx} label={skill} size="small" variant="outlined" />
                        ))}
                        {metadata.extractedSkills.length > 20 && (
                          <Chip
                            label={`+${metadata.extractedSkills.length - 20} more`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Action Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Please update your job description above and try again
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  // Fallback: Display plain error message if not structured
  const errorMessage = typeof error === 'string' 
    ? error 
    : error?.error || error?.message || 'An error occurred during analysis.';
  
  return (
    <Alert
      severity="error"
      sx={{
        mt: 3,
        width: '100%',
        borderRadius: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
      onClose={onDismiss}
    >
      <AlertTitle>Analysis Failed</AlertTitle>
      {errorMessage}
    </Alert>
  );
};

export default ValidationErrorDisplay;
